import React from 'react';

const Section1Architecture = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-indigo-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">🏗️</span>
      <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
        Architecture Overview
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">The entire AI system at a glance</p>

    {/* Key stats bar */}
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
      {[
        { icon: '🤖', value: '3', label: 'AI Models' },
        { icon: '🎨', value: '200+', label: 'Themes (RAG Embedded)' },
        { icon: '🏪', value: '250+', label: 'Vendors (AI Profiled)' },
        { icon: '📿', value: '105', label: 'Rituals (RAG Embedded)' },
        { icon: '🔧', value: '7', label: 'Agent Tools' },
        { icon: '🧪', value: '2', label: 'Live A/B Experiments' },
      ].map((stat, i) => (
        <div key={i} className="bg-gradient-to-br from-indigo-50 to-violet-50/50 rounded-2xl p-4 text-center border border-indigo-200/50 hover:scale-105 transition-transform duration-300">
          <span className="text-2xl block mb-1">{stat.icon}</span>
          <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
        </div>
      ))}
    </div>

    {/* Architecture flow diagram */}
    <div className="bg-gradient-to-br from-indigo-50/50 to-violet-50/50 rounded-2xl p-6 border border-indigo-200/30">
      <h3 className="text-base font-bold text-indigo-600 uppercase tracking-wider mb-6 text-center">System Architecture Flow</h3>

      <div className="flex flex-col items-center">
        <div className="flex flex-wrap justify-center gap-4 mb-4">
          {[
            { icon: '📝', label: 'Dream Wedding Generator', sub: 'Text Input' },
            { icon: '📸', label: 'Moodboard Matcher', sub: 'Image Upload' },
            { icon: '💬', label: 'VowBot Agent', sub: 'Chat Query' },
          ].map((input, i) => (
            <div key={i} className="bg-white rounded-xl px-5 py-3 shadow-md border border-indigo-200/50 text-center">
              <span className="text-xl block">{input.icon}</span>
              <p className="text-sm font-bold text-gray-700 mt-1">{input.label}</p>
              <p className="text-xs text-gray-400">{input.sub}</p>
            </div>
          ))}
        </div>

        <div className="w-0.5 h-6 bg-gradient-to-b from-indigo-300 to-purple-400"></div>
        <div className="text-indigo-400 text-xs mb-1">▼</div>

        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl px-8 py-4 shadow-lg border border-indigo-200/50 mb-4 w-full max-w-lg text-center">
          <p className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-2">Multi-Model AI Engine</p>
          <div className="flex justify-center gap-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm">Gemini 2.0</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm">Claude 3.5</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm">GPT-4o-mini</span>
          </div>
        </div>

        <div className="w-0.5 h-4 bg-gradient-to-b from-purple-300 to-pink-400"></div>
        <div className="text-purple-400 text-xs mb-1">▼</div>

        <div className="flex flex-wrap justify-center gap-4 mb-4">
          {[
            { icon: '🔍', label: 'RAG Pipeline', sub: 'pgvector · 1536-dim', bg: 'from-emerald-50 to-teal-50', border: 'emerald' },
            { icon: '✍️', label: 'Prompt Engine', sub: '5-layer validation', bg: 'from-rose-50 to-pink-50', border: 'rose' },
            { icon: '🧪', label: 'A/B Testing', sub: '3 variants · round-robin', bg: 'from-amber-50 to-orange-50', border: 'amber' },
            { icon: '📊', label: 'Evaluation', sub: '5-dimension scoring', bg: 'from-blue-50 to-indigo-50', border: 'blue' },
          ].map((layer, i) => (
            <div key={i} className={`bg-gradient-to-br ${layer.bg} rounded-xl px-4 py-3 shadow-md border border-${layer.border}-200/50 text-center`}>
              <span className="text-lg block">{layer.icon}</span>
              <p className="text-sm font-bold text-gray-700 mt-1">{layer.label}</p>
              <p className="text-xs text-gray-400">{layer.sub}</p>
            </div>
          ))}
        </div>

        <div className="w-0.5 h-4 bg-gradient-to-b from-pink-300 to-rose-400"></div>
        <div className="text-rose-400 text-xs mb-1">▼</div>

        <div className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-2xl px-8 py-4 shadow-lg border border-rose-200/50 mb-4 w-full max-w-2xl text-center">
          <p className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2">Structured Theme Output</p>
          <p className="text-xs text-gray-500 mb-3">AI output isn't just text. It's a structured profile that feeds vendor matching, ritual suggestions, and every feature below</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Theme Name', 'Description', 'Colors', 'Styles', 'Mood', 'Formality', 'Key Elements'].map((dim, i) => (
              <span key={i} className="px-2 py-1 rounded-lg text-xs font-bold bg-white/80 text-gray-600 border border-rose-200/50">{dim}</span>
            ))}
          </div>
        </div>

        <div className="w-0.5 h-4 bg-gradient-to-b from-rose-300 to-gold-400"></div>
        <div className="text-rose-400 text-xs mb-1">▼</div>

        <div className="flex flex-wrap justify-center gap-3">
          {[
            { icon: '🏪', label: 'Vendor Matching', sub: 'Cosine similarity · 50ms' },
            { icon: '📿', label: 'Ritual Suggestions', sub: 'RAG · multi-factor scoring' },
            { icon: '🤖', label: 'VowBot Agent', sub: '7 tools · function calling' },
          ].map((feature, i) => (
            <div key={i} className="bg-white rounded-xl px-4 py-3 shadow-md border border-gray-200/50 text-center hover:scale-105 transition-transform duration-300">
              <span className="text-lg block">{feature.icon}</span>
              <p className="text-sm font-bold text-gray-700 mt-1">{feature.label}</p>
              <p className="text-xs text-gray-400">{feature.sub}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-2 text-gray-400">
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-teal-300"></div>
          <span className="text-xs font-bold text-teal-500">↻ Feedback Loop</span>
          <div className="w-16 h-0.5 bg-gradient-to-r from-teal-300 to-transparent"></div>
        </div>
        <p className="text-xs text-gray-400 mt-1">Ratings, thumbs, saves, time spent, Vendor & Ritual funnels, session tracking → Model Evaluation → A/B Testing → Prompt Optimization</p>
      </div>
    </div>

    {/* Engineering decision callout */}
    <div className="mt-6 bg-gradient-to-r from-indigo-50/80 to-violet-50/80 rounded-2xl p-5 border border-indigo-200/30">
      <h3 className="text-base font-bold text-indigo-600 uppercase tracking-wider mb-3">🎯 Key Engineering Decisions</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Why 3 models instead of 1?', a: 'Each model has a genuine strength. OpenAI is fastest (793ms) and cheapest ($0.002/req), Gemini scores highest on automated quality (90.4 avg), Claude produces the most creative descriptions. Compare Mode lets users see the difference firsthand.' },
          { q: 'Why RAG for rituals but cosine similarity for vendors?', a: 'Rituals have unstructured text needing semantic understanding (150-200ms). Vendors have structured 6-dimensional vectors, so cosine similarity is 3x faster (50ms).' },
          { q: 'Why round-robin instead of random A/B assignment?', a: 'Random assignment with small sample sizes caused uneven distribution (12/22/20 split). Round-robin guarantees even A→B→C→A cycling.' },
          { q: 'Why function calling over direct RAG injection for VowBot?', a: 'The agent decides when to search, which is more efficient than injecting context on every query. Only ~60% of queries need tool calls.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-indigo-100/50">
            <p className="text-sm font-bold text-indigo-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Section1Architecture;
